import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../services/api';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Typography } from '@mui/material';
import './RuddrContactsDialog.css';
import Cookies from 'js-cookie';

const DeleteIcon = apiService.getBlobAssetUrl('delete_A1.svg');
const AddIcon = apiService.getBlobAssetUrl('Add_Icon.svg');

const ManageRuddrContactsDialog = ({ open, onClose, onSave, project, mode = 'add' }) => {
  const [contacts, setContacts] = useState([]);
  const [initialContacts, setInitialContacts] = useState([]);
  const [deletedEmails, setDeletedEmails] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [contactErrors, setContactErrors] = useState({});
  const [existingContacts, setExistingContacts] = useState([]);
  const accessToken = Cookies.get('access_token');

  useEffect(() => {
    const fetchContacts = async () => {
      if (!open || !project?.id) return;

      try {
        const response = await apiService.fetchData(`projects/customer-contacts/${project.id}`);
        let fetchedContacts = response?.contacts || [];

        if (!Array.isArray(fetchedContacts)) {
          if (response?.data && Array.isArray(response.data)) {
            fetchedContacts = response.data;
          } else if (Array.isArray(response)) {
            fetchedContacts = response;
          }
        }

        if (mode === 'edit') {
          if (fetchedContacts.length === 0) {
            const defaultContact = [{ id: `${Date.now()}-0`, fullName: '', email: '', contactId: null }];
            setContacts(defaultContact);
            setInitialContacts(JSON.parse(JSON.stringify(defaultContact)));
            setError('No existing contacts found. Add a new contact to proceed.');
          } else {
            const mappedContacts = fetchedContacts.map((contact, index) => ({
              id: contact.ID || `${Date.now()}-${index}`,
              fullName: contact.full_name || contact.FirstName || '',
              email: contact.email || contact.Email || '',
              contactId: contact.ID || null,
            }));
            setContacts(mappedContacts);
            setInitialContacts(JSON.parse(JSON.stringify(mappedContacts)));
          }
        } else {
          setContacts([{ id: `${Date.now()}-0`, fullName: '', email: '' }]);
          setExistingContacts(
            fetchedContacts.map((contact) => ({
              fullName: contact.full_name || contact.FirstName || '',
              email: contact.email || contact.Email || '',
            }))
          );
        }
        setError(null);
        setContactErrors({});
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setError(`Failed to load contacts: ${err.message}`);
        const defaultContact = [{ id: `${Date.now()}-0`, fullName: '', email: '', contactId: null }];
        setContacts(defaultContact);
        if (mode === 'edit') {
          setInitialContacts(JSON.parse(JSON.stringify(defaultContact)));
        }
      }
    };

    fetchContacts();
  }, [open, project, mode]);

  const handleContactChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContacts = [...contacts];
    updatedContacts[index][name] = value;

    const currentErrors = contactErrors[index] || {};

    if (name === 'fullName') {
      const trimmedValue = value.trim();
      const namePattern = /^[a-zA-Z0-9\s_-]+$/;
      if (!trimmedValue) {
        currentErrors.fullName = 'Full Name cannot be empty or contain only spaces.';
      } else if (!namePattern.test(trimmedValue)) {
        currentErrors.fullName = 'Full Name can only contain letters, numbers, spaces, hyphens, or underscores.';
      } else {
        currentErrors.fullName = null;
      }
    }

    if (name === 'email') {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailPattern.test(value)) {
        currentErrors.email = 'Please enter a valid email address (e.g., user@example.com).';
      } else {
        currentErrors.email = null;
      }
    }

    const currentContact = updatedContacts[index];
    if (currentContact.fullName && currentContact.email) {
      const trimmedFullName = currentContact.fullName.trim();
      const isDuplicateInCurrent = updatedContacts.some(
        (contact, i) =>
          i !== index &&
          contact.fullName.trim() === trimmedFullName &&
          contact.email === currentContact.email
      );
      const isDuplicateInExisting = mode === 'add' && existingContacts.some(
        (contact) =>
          contact.fullName.trim() === trimmedFullName &&
          contact.email === currentContact.email
      );
      if (isDuplicateInCurrent || isDuplicateInExisting) {
        currentErrors.duplicate = 'Contact already exists.';
      } else {
        currentErrors.duplicate = null;
      }
    } else {
      currentErrors.duplicate = null;
    }

    setContactErrors((prev) => ({
      ...prev,
      [index]: currentErrors.fullName || currentErrors.email || currentErrors.duplicate ? currentErrors : null,
    }));

    const allValid = updatedContacts.every((contact, i) => {
      const trimmedFullName = contact.fullName.trim();
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const namePattern = /^[a-zA-Z0-9\s_-]+$/;
      const hasNoErrors = !contactErrors[i]?.fullName && !contactErrors[i]?.email && !contactErrors[i]?.duplicate;
      return trimmedFullName && emailPattern.test(contact.email) && namePattern.test(trimmedFullName) && hasNoErrors;
    });
    if (allValid) {
      setError(null);
    }

    setContacts(updatedContacts);
  };

  const handleAddNewContact = () => {
    setContacts([...contacts, { id: `${Date.now()}-${contacts.length}`, fullName: '', email: '', contactId: null }]);
    setError(null);
  };

  const handleDeleteContact = (index) => {
    if (mode === 'add') {
      if (contacts.length === 1) {
        const updatedContacts = [...contacts];
        updatedContacts[index] = { ...updatedContacts[index], fullName: '', email: '' };
        setContacts(updatedContacts);
        setContactErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
        setError(null);
      } else {
        const updatedContacts = contacts.filter((_, i) => i !== index);
        setContacts(updatedContacts);
        setContactErrors((prev) => {
          const newErrors = {};
          Object.keys(prev).forEach((key) => {
            const keyInt = parseInt(key, 10);
            if (keyInt < index) {
              newErrors[keyInt] = prev[keyInt];
            } else if (keyInt > index) {
              newErrors[keyInt - 1] = prev[keyInt];
            }
          });
          return newErrors;
        });
        setError(null);
      }
    } else {
      if (contacts.length <= 1) {
        setError('At least one contact is required.');
        return;
      }
      const contactToDelete = contacts[index];
      if (contactToDelete.contactId && contactToDelete.email) {
        setDeletedEmails([...deletedEmails, contactToDelete.email]);
      }
      const updatedContacts = contacts.filter((_, i) => i !== index);
      setContacts(updatedContacts);
      setError(null);
      setContactErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleCancel = () => {
    setContacts([]);
    setDeletedEmails([]);
    setError(null);
    setContactErrors({});
    setSubmitting(false);
    setExistingContacts([]);
    onClose();
  };

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const hasErrors = Object.values(contactErrors).some(
        (error) => error && (error.fullName || error.email || error.duplicate)
      );
      if (hasErrors) {
        throw new Error('Please fix all invalid fields before saving.');
      }

      const seen = new Set();
      const hasDuplicates = contacts.some((contact) => {
        const trimmedFullName = contact.fullName.trim();
        if (!trimmedFullName || !contact.email) return false;
        const key = `${trimmedFullName}-${contact.email}`;
        if (seen.has(key)) return true;
        seen.add(key);
        return false;
      });

      if (mode === 'add') {
        const hasExistingDuplicates = contacts.some((contact) => {
          const trimmedFullName = contact.fullName.trim();
          if (!trimmedFullName || !contact.email) return false;
          return existingContacts.some(
            (existing) =>
              existing.fullName.trim() === trimmedFullName &&
              existing.email === contact.email
          );
        });
        if (hasDuplicates || hasExistingDuplicates) {
          setError('Contact already exists.');
          throw new Error('Contact already exists.');
        }
      } else if (hasDuplicates) {
        setError('Contact already exists.');
        throw new Error('Contact already exists.');
      }

      setError(null);

      const projectData = {
        project_customer_contacts: contacts
          .filter((contact) => contact.fullName.trim() && contact.email)
          .map((contact) => ({
            id: contact.contactId,
            full_name: contact.fullName.trim(),
            email: contact.email,
          })),
      };

      if (projectData.project_customer_contacts.length === 0 && (mode === 'add' || deletedEmails.length === 0)) {
        throw new Error('At least one valid contact is required.');
      }

      if (mode === 'add') {
        await apiService.postData(`projects/customer-contacts/${project?.id}`, projectData, accessToken);
      } else {
        const contactsToUpdate = projectData.project_customer_contacts.filter((c) => c.id);
        const contactsToCreate = projectData.project_customer_contacts.filter((c) => !c.id);

        if (contactsToUpdate.length > 0) {
          const updatePayload = { project_customer_contacts: contactsToUpdate };
          await apiService.putData(`projects/customer-contacts/${project?.id}`, updatePayload, accessToken);
        }

        if (contactsToCreate.length > 0) {
          const createPayload = { project_customer_contacts: contactsToCreate };
          await apiService.postData(`projects/customer-contacts/${project?.id}`, createPayload, accessToken);
        }

        if (deletedEmails.length > 0) {
          await apiService.deleteData(`projects/customer-contacts/${project?.id}`, { email_ids: deletedEmails }, accessToken);
        }
      }

      const response = await apiService.fetchData(`projects/customer-contacts/${project?.id}`);
      const userId = sessionStorage.getItem('user_id');
      const practiceId = sessionStorage.getItem('practice_id');
      await apiService.fetchData(`projects/${userId}/${practiceId}/ruddr`);

      const updatedContacts = response?.contacts || response?.project_customer_contacts || [];
      const updatedProjectData = {
        project_id: project?.id,
        updated_fields: {
          project_customer_contacts: updatedContacts.map((contact) => ({
            id: contact.ID || contact.id,
            full_name: contact.full_name || contact.FirstName,
            email: contact.email || contact.Email,
          })),
        },
      };

      await onSave(updatedProjectData);
      setContacts([]);
      setDeletedEmails([]);
      setError(null);
      setContactErrors({});
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Error saving contacts:', err);
      setError(`Failed to save contacts: ${err.message}`);
      setSubmitting(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (mode === 'add') return true;
    if (deletedEmails.length > 0) return true;
    if (contacts.length !== initialContacts.length) return true;
    return contacts.some((contact, index) => {
      const initial = initialContacts[index];
      if (!initial) return true;
      return contact.fullName !== initial.fullName || contact.email !== initial.email;
    });
  }, [contacts, initialContacts, deletedEmails, mode]);

  const dialogTitle = `Manage Contacts for ${project?.['Project Name'] || 'Project'}`;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      className="Project-dialog"
    >
      <DialogTitle className="Project-dialog-title">
        {dialogTitle}
        <button className="Project-close-btn" onClick={handleCancel}>
          ×
        </button>
      </DialogTitle>
      <DialogContent className="Project-dialog-content">
        {error && <Typography color="error">{error}</Typography>}
        <Typography variant="h6" className="Project-dialog-section-title">Contacts</Typography>

        {contacts.map((contact, index) => (
          <div className="Project-contact-row" key={contact.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <TextField
              name="fullName"
              label="Full Name"
              variant="outlined"
              fullWidth
              value={contact.fullName}
              onChange={(e) => handleContactChange(index, e)}
              placeholder="Type here..."
              InputLabelProps={{ shrink: true }}
              required
              disabled={submitting}
              error={!!contactErrors[index]?.fullName}
              helperText={contactErrors[index]?.fullName}
            />
            <TextField
              name="email"
              label="Email ID"
              type="email"
              variant="outlined"
              fullWidth
              value={contact.email}
              onChange={(e) => handleContactChange(index, e)}
              placeholder="Type here..."
              InputLabelProps={{ shrink: true }}
              required
              disabled={submitting}
              error={!!contactErrors[index]?.email || !!contactErrors[index]?.duplicate}
              helperText={contactErrors[index]?.email || contactErrors[index]?.duplicate}
            />
            <Button
              className="Project-delete-contact-button"
              onClick={() => handleDeleteContact(index)}
              disabled={submitting || (mode === 'edit' && contacts.length <= 1)}
            >
              <img src={DeleteIcon} alt="Delete Icon" className="Project-delete-icon" />
            </Button>
          </div>
        ))}

        <Button className="Project-add-new-button" onClick={handleAddNewContact} disabled={submitting}>
          <img src={AddIcon} alt="Add Icon" className="Project-add-icon" /> Add New
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="primary" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={
            submitting ||
            (mode === 'edit' && !hasChanges) ||
            (contacts.some((c) => !c.fullName.trim() || !c.email || (contactErrors[c.id] && (contactErrors[c.id].fullName || contactErrors[c.id].email || contactErrors[c.id].duplicate))) &&
              (mode === 'add' || deletedEmails.length === 0))
          }
          sx={{
            background: '#0066D1',
            color: 'white',
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            padding: '8px 32px',
            borderRadius: '8px',
            border: 'none',
            '&:hover': {
              background: '#034589',
            },
            '&.Mui-disabled': {
              background: '#e0e0e0',
              color: '#666666',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ManageRuddrContactsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  project: PropTypes.object,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
};

ManageRuddrContactsDialog.defaultProps = {
  project: {},
};

export default ManageRuddrContactsDialog;
